import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface FooterLogoManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  logo?: any;
}

const FooterLogoManagementDialog = ({ open, onOpenChange, logo }: FooterLogoManagementDialogProps) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState<string>('');
  const { register, handleSubmit, reset, setValue, watch } = useForm();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (logo) {
      reset({
        name: logo.name,
        link_url: logo.link_url || '',
        alt_text: logo.alt_text,
        display_order: logo.display_order,
        is_active: logo.is_active,
      });
      setUploadedUrl(logo.image_url);
    } else {
      reset({
        name: '',
        link_url: '',
        alt_text: '',
        display_order: 0,
        is_active: true,
      });
      setUploadedUrl('');
    }
  }, [logo, reset]);

  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      if (logo) {
        // Update existing
        const { error } = await supabase
          .from('footer_logos')
          .update(data)
          .eq('id', logo.id);
        
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('footer_logos')
          .insert([data]);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-footer-logos'] });
      queryClient.invalidateQueries({ queryKey: ['footer-logos'] });
      toast({ title: logo ? "Logo updated" : "Logo added" });
      onOpenChange(false);
    },
    onError: () => {
      toast({ 
        title: "Failed to save logo", 
        variant: "destructive" 
      });
    },
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('footer-logos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('footer-logos')
        .getPublicUrl(filePath);

      setUploadedUrl(publicUrl);
      toast({ title: "Image uploaded successfully" });
    } catch (error) {
      toast({ 
        title: "Failed to upload image", 
        variant: "destructive" 
      });
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = (data: any) => {
    if (!uploadedUrl) {
      toast({ 
        title: "Please upload an image", 
        variant: "destructive" 
      });
      return;
    }

    saveMutation.mutate({
      ...data,
      image_url: uploadedUrl,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>{logo ? 'Edit' : 'Add'} Footer Logo</DialogTitle>
          <DialogDescription>
            {logo ? 'Update' : 'Add'} certification or partnership logo for the footer.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="image">Logo Image</Label>
            <div className="mt-2 space-y-2">
              {uploadedUrl && (
                <div className="border rounded-lg p-4 flex justify-center bg-muted">
                  <img 
                    src={uploadedUrl} 
                    alt="Preview" 
                    className="max-h-20 object-contain"
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="cursor-pointer"
                />
                {uploading && <span className="text-sm text-muted-foreground">Uploading...</span>}
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              {...register('name', { required: true })}
              placeholder="e.g., Cyber Essentials"
            />
          </div>

          <div>
            <Label htmlFor="alt_text">Alt Text (for accessibility)</Label>
            <Input
              id="alt_text"
              {...register('alt_text', { required: true })}
              placeholder="e.g., Cyber Essentials Certified"
            />
          </div>

          <div>
            <Label htmlFor="link_url">Link URL (optional)</Label>
            <Input
              id="link_url"
              {...register('link_url')}
              placeholder="https://example.com"
            />
          </div>

          <div>
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              {...register('display_order', { valueAsNumber: true })}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={watch('is_active')}
              onCheckedChange={(checked) => setValue('is_active', checked)}
            />
            <Label htmlFor="is_active">Active (visible on website)</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMutation.isPending || uploading}>
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FooterLogoManagementDialog;